import React, { createContext, useContext, useState, useEffect } from 'react';
import ApiService from '../services/api';
import { useAuth } from './AuthContext';

const CompanyContext = createContext();

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};

export const CompanyProvider = ({ children }) => {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch user's companies
  const fetchCompanies = async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await ApiService.get('/user/companies');
      if (response.success) {
        setCompanies(response.data || []);
        // Set primary company as current if exists
        const primary = response.data?.find(c => c.pivot?.is_primary);
        if (primary) {
          setCurrentCompany(primary);
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch companies');
      console.error('Error fetching companies:', err);
    } finally {
      setLoading(false);
    }
  };

  // Create a new company
  const createCompany = async (companyData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await ApiService.post('/companies', companyData);
      if (response.success) {
        await fetchCompanies(); // Refresh list
        return { success: true, data: response.data };
      }
      return { success: false, message: response.message };
    } catch (err) {
      const errorMsg = err.data?.message || err.message || 'Failed to create company';
      setError(errorMsg);
      return { success: false, message: errorMsg, errors: err.data?.errors };
    } finally {
      setLoading(false);
    }
  };

  // Update company
  const updateCompany = async (companyId, companyData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await ApiService.put(`/companies/${companyId}`, companyData);
      if (response.success) {
        await fetchCompanies(); // Refresh list
        return { success: true, data: response.data };
      }
      return { success: false, message: response.message };
    } catch (err) {
      const errorMsg = err.data?.message || err.message || 'Failed to update company';
      setError(errorMsg);
      return { success: false, message: errorMsg, errors: err.data?.errors };
    } finally {
      setLoading(false);
    }
  };

  // Follow/unfollow company
  const followCompany = async (companyId) => {
    try {
      const response = await ApiService.post(`/companies/${companyId}/follow`);
      return response.success;
    } catch (err) {
      console.error('Error following company:', err);
      return false;
    }
  };

  const unfollowCompany = async (companyId) => {
    try {
      const response = await ApiService.delete(`/companies/${companyId}/follow`);
      return response.success;
    } catch (err) {
      console.error('Error unfollowing company:', err);
      return false;
    }
  };

  useEffect(() => {
    if (user) {
      fetchCompanies();
    }
  }, [user]);

  const value = {
    companies,
    currentCompany,
    setCurrentCompany,
    loading,
    error,
    fetchCompanies,
    createCompany,
    updateCompany,
    followCompany,
    unfollowCompany,
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};

